import * as uuid from 'uuid';

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import {
  ALPHA_REGEX,
  ALPHANUMERIC_REGEX,
  EMAIL_REGEXP,
  EMOJI_REGEX,
  HTTP_URL_REGEXP,
  IP_ADDRESS_REGEXP,
  NUMERIC_REGEX,
  PASSWORD_REGEX,
  PORT_REGEXP,
  RTMP_URL_REGEXP,
  RTSP_URL_REGEXP,
  TIME_REGEX,
  WS_URL_REGEXP,
} from '@lib/common/constants';

import { SnFormUrlValidatorType } from './types/form-url-validator.type';

export class SnValidators {
  public static email(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !EMAIL_REGEXP.test(value) ? { email: true } : null;
  }

  public static ipAddress(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !IP_ADDRESS_REGEXP.test(value) ? { ipAddress: true } : null;
  }

  public static port(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !PORT_REGEXP.test(value) ? { port: true } : null;
  }

  public static url(type: SnFormUrlValidatorType = 'http'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value == null || value.length === 0) {
        return null;
      }

      switch (type) {
        case 'rtmp':
          return !RTMP_URL_REGEXP.test(value) ? { url: true } : null;

        case 'rtsp':
          return !RTSP_URL_REGEXP.test(value) ? { url: true } : null;

        case 'ws':
          return !WS_URL_REGEXP.test(value) ? { url: true } : null;

        default:
          return !HTTP_URL_REGEXP.test(value) ? { url: true } : null;
      }
    };
  }

  public static uniqueByList(
    collection: Set<string | number> | (string | number)[],
    valueHandler?: (value: string) => string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control.value;

      if (value == null || value.length === 0) {
        return null;
      }

      if (valueHandler) {
        value = valueHandler(value);
      }

      if (Array.isArray(collection)) {
        return collection.includes(value) ? { unique: true } : null;
      }

      return collection.has(value) ? { unique: true } : null;
    };
  }

  public static password(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    return !PASSWORD_REGEX.test(value) ? { password: true } : null;
  }

  public static alphanumeric(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !ALPHANUMERIC_REGEX.test(value) ? { alphanumeric: true } : null;
  }

  public static alpha(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !ALPHA_REGEX.test(value) ? { alpha: true } : null;
  }

  public static numeric(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value == null || value.length === 0) {
      return null;
    }
    return !NUMERIC_REGEX.test(value) ? { numeric: true } : null;
  }
  public static time(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    return !TIME_REGEX.test(value) ? { time: true } : null;
  }

  public static noWhitespace(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const isWhitespace = typeof control.value === 'string' && control.value.trim().length === 0;
    return isWhitespace ? { noWhitespace: true } : null;
  }

  public static maxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value == null || value.length === 0) {
        return null; // Let required validator handle empty values
      }

      const actualLength = typeof value === 'string' ? value.length : 0;

      return actualLength > maxLength
        ? {
            maxLength: {
              actualLength,
              requiredLength: maxLength,
            },
          }
        : null;
    };
  }

  public static minLength(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value == null || value.length === 0) {
        return null; // Let required validator handle empty values
      }

      const actualLength = typeof value === 'string' ? value.length : 0;
      return actualLength < minLength
        ? {
            minLength: {
              actualLength,
              requiredLength: minLength,
            },
          }
        : null;
    };
  }

  public static characterCount(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const actualLength = typeof value === 'string' ? value.length : 0;
      const remaining = maxLength - actualLength;

      // Always return an error with character count info
      // This will always show the message, but we'll style it as informational
      return {
        characterCount: {
          actualLength,
          maxLength,
          remaining: Math.max(0, remaining),
          isOverLimit: actualLength > maxLength,
        },
      };
    };
  }

  public static hasEmojis(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null; // Let required validator handle empty values
    }

    if (typeof value !== 'string') {
      return null;
    }

    const containsEmojis = EMOJI_REGEX.test(value);
    return containsEmojis ? { hasEmojis: true } : null;
  }

  public static uuid(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) return null; // Let required validator handle empty values
    if (typeof value !== 'string') return null;

    const normalized = /^[0-9A-Fa-f]{32}$/.test(value)
      ? value.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5')
      : value;

    return uuid.validate(normalized) ? null : { uuid: true };
  }
}
